/**
 * LLM Chat Service
 * Handles real-time chat messaging with LLM backend
 * All endpoints use VITE_LLM_URL
 */

import apiClient from "./client";

// LLM API Base URL - required for chat messaging
const LLM_BASE_URL = import.meta.env.VITE_LLM_URL;

if (!LLM_BASE_URL) {
  console.error("VITE_LLM_URL is not configured in environment variables");
}

// Common headers for all LLM API requests
const LLM_HEADERS = {
  "X-App-Source": "extension",
};

export interface LLMMessageRequest {
  chat_id: string;
  message: string;
  files?: File[];
}

export interface LLMMessageResponse {
  dataCollectionNeeded: boolean;
  answer: string;
  message_id: string;
  chat_id?: string; // Backend may return chat_id for new chats
}

export class LLMChatService {
  /**
   * Send message to LLM backend
   * Uses FormData to match extension's format exactly
   */
  static async sendMessage(
    request: LLMMessageRequest,
  ): Promise<LLMMessageResponse> {
    const formData = new FormData();

    // Required fields matching extension format
    formData.append("chat_id", request.chat_id);
    formData.append("message", request.message);

    // For non-autonomous mode, always false
    formData.append("is_context", "false");
    formData.append("context", "{}");

    // Add files if present
    if (request.files && request.files.length > 0) {
      request.files.forEach((file) => {
        formData.append("files", file);
      });
    }

    console.log("Sending message to LLM:", {
      chat_id: request.chat_id,
      message: request.message.substring(0, 100),
      files: request.files?.length || 0,
    });

    const response = await apiClient.request<LLMMessageResponse>("/chat", {
      method: "POST",
      headers: LLM_HEADERS,
      body: formData,
      baseUrl: LLM_BASE_URL,
    });

    console.log("LLM response received:", {
      dataCollectionNeeded: response.dataCollectionNeeded,
      messageId: response.message_id,
      answerLength:
        typeof response.answer === "string" ? response.answer.length : 0,
    });

    // Only handle direct answers (dataCollectionNeeded: false)
    if (response.dataCollectionNeeded) {
      throw new Error(
        "Autonomous mode not supported. Please ask a direct question.",
      );
    }

    return response;
  }
}
